/* eslint-disable max-len */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  RichTextEditorComponent,
  Toolbar,
  Inject,
  Link,
  Image,
  HtmlEditor,
  QuickToolbar,
  ToolbarSettingsModel,
} from '@syncfusion/ej2-react-richtexteditor'
import { Modal } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import { useAppSelector } from '@store/hooks'
import { getFullName } from '@utils/fullname.util'
import { VariableList } from '@components/pages/Messages/MockDataMessages'
import EmojiPicker from 'emoji-picker-react'
import {
  AI_ICON_COLOR,
  generateAIToolbarButtons,
  generateAIToolbarStyles,
} from './AIToolbar.utils'

export type CustomTemplateForModal = {
  id: string
  name: string
  content: string
  category?: string
}

/** Backend template used to populate "Select template" and apply content into the editor */
export type BackendTemplateForModal = {
  id: string
  name: string
  content: string
  category?: string
}

type CustomTemplateModalProps = {
  isOpen: boolean
  onClose: () => void
  onSubmit?: (payload: {
    name: string
    content: string
    /** Category string (e.g. Anesthesia) used when creating/updating; derived from selected backend template if any */
    templateId?: string
    existingTemplateId?: string
  }) => void
  /** Called when user confirms delete of the template being edited (only when initialTemplate is set) */
  onDelete?: (templateId: string) => void
  /** When set, modal is in edit mode and submit will send existingTemplateId */
  initialTemplate?: CustomTemplateForModal | null
  /** Templates from backend; dropdown options and applying a selection loads its content into the editor */
  backendTemplates?: BackendTemplateForModal[]
}

const CustomTemplateModal: React.FC<CustomTemplateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  initialTemplate = null,
  backendTemplates = [],
}) => {
  const [templateName, setTemplateName] = useState('')
  const [templateContent, setTemplateContent] = useState('')
  /** Selected backend template id; empty string = no selection (initial state) */
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('')
  /** When set, show confirm modal: discard current content and apply this template */
  const [templateToApply, setTemplateToApply] =
    useState<BackendTemplateForModal | null>(null)
  /** When true, show confirm modal for deleting the current template */
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [activePopover, setActivePopover] = useState<
    'emoji' | 'variable' | 'mention' | 'magic' | null
  >(null)
  const rteRef = useRef<RichTextEditorComponent>(null)
  const editorRef = useRef<HTMLDivElement>(null)

  const patients = useAppSelector((state) => state.patient.data)

  const mentionOptions = useMemo(() => {
    return patients
      .map((patient) => getFullName(patient))
      .filter((name): name is string => !!name && name.trim().length > 0)
  }, [patients])

  const templateOptions = useMemo((): Array<{
    value: string
    label: string
  }> => {
    const options: Array<{ value: string; label: string }> = [
      { value: '', label: 'No selection' },
    ]
    backendTemplates.forEach((t) => {
      options.push({ value: t.id, label: t.name })
    })
    return options
  }, [backendTemplates])

  const handleTogglePopover = useCallback(
    (type: 'emoji' | 'variable' | 'mention' | 'magic') => {
      setActivePopover((prev) => (prev === type ? null : type))
    },
    [],
  )

  const handleToolbarAction = useCallback(
    (key: string) => {
      switch (key) {
        case 'happy':
          handleTogglePopover('emoji')
          break
        case 'mention':
          handleTogglePopover('mention')
          break
        case 'variable':
          handleTogglePopover('variable')
          break
        case 'magic-edit':
          handleTogglePopover('magic')
          break
        default:
          break
      }
    },
    [handleTogglePopover],
  )

  const toolbarSettings: ToolbarSettingsModel = useMemo(() => {
    const baseItems = [
      'Undo',
      'Redo',
      '|',
      'Bold',
      'Italic',
      'Underline',
      '|',
      'Alignments',
      '|',
      'NumberFormatList',
      'BulletFormatList',
      '|',
      'Outdent',
      'Indent',
    ]

    const aiButtons = generateAIToolbarButtons(handleToolbarAction).filter(
      (button) => !['template', 'signature'].includes(button.id),
    )

    return {
      items: [...baseItems, ...aiButtons],
      enable: true,
    }
  }, [handleToolbarAction])

  useEffect(() => {
    if (isOpen) {
      if (initialTemplate) {
        setTemplateName(initialTemplate.name)
        setTemplateContent(initialTemplate.content)
        setSelectedTemplateId('')
        setActivePopover(null)
      } else {
        setTemplateName('')
        setTemplateContent('')
        setSelectedTemplateId('')
        setActivePopover(null)
      }
    } else {
      setTemplateName('')
      setTemplateContent('')
      setSelectedTemplateId('')
      setActivePopover(null)
    }
  }, [isOpen, initialTemplate])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        editorRef.current &&
        !editorRef.current.contains(event.target as Node)
      ) {
        setActivePopover(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSubmit = (): void => {
    const currentContent = rteRef.current?.value ?? templateContent
    if (!templateName.trim()) return
    const categoryForSubmit =
      selectedTemplateId &&
      backendTemplates.find((t) => t.id === selectedTemplateId)?.category
    onSubmit?.({
      name: templateName.trim(),
      content: currentContent,
      templateId: categoryForSubmit,
      existingTemplateId: initialTemplate?.id,
    })
    setTemplateContent(currentContent)
    onClose()
  }

  const handleTemplateSelectChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>): void => {
      const newId = event.target.value
      if (newId === '') {
        setSelectedTemplateId('')
        return
      }
      const template = backendTemplates.find((t) => t.id === newId)
      if (!template) return
      const currentContent = (rteRef.current?.value ?? templateContent).trim()
      if (currentContent !== '') {
        setTemplateToApply(template)
        return
      }
      setTemplateContent(template.content)
      setSelectedTemplateId(newId)
    },
    [backendTemplates, templateContent],
  )

  const handleConfirmApplyTemplate = useCallback((): void => {
    if (!templateToApply) return
    setTemplateContent(templateToApply.content)
    setSelectedTemplateId(templateToApply.id)
    setTemplateToApply(null)
  }, [templateToApply])

  const syncTemplateContent = useCallback(() => {
    const editor = rteRef.current
    if (!editor) return
    setTemplateContent(editor.value ?? '')
  }, [])

  const insertAtCursor = useCallback(
    (text: string) => {
      const editor = rteRef.current
      if (!editor) return

      editor.focusIn()
      editor.executeCommand('insertText', text)
      syncTemplateContent()
    },
    [syncTemplateContent],
  )

  const handleEmojiSelect = (emoji: string): void => {
    insertAtCursor(emoji)
    setActivePopover(null)
  }

  const handleVariableSelect = (value: string): void => {
    insertAtCursor(value)
    setActivePopover(null)
  }

  const handleMentionSelect = (value: string): void => {
    insertAtCursor(`@${value}`)
    setActivePopover(null)
  }

  const handleMagicEdit = (command: 'reply' | 'summarize'): void => {
    let prompt = ''
    switch (command) {
      case 'reply':
        prompt = 'Generate a response for the following message:\n '
        break
      case 'summarize':
        prompt = `Summarize the message below. Make sure to retain the same context with a shorter number of characters.\n `
        break
      default:
        break
    }

    if (prompt) {
      setTemplateContent(prompt)
      requestAnimationFrame(() => {
        rteRef.current?.focusIn()
      })
    }
    setActivePopover(null)
  }

  return (
    <>
      <Modal
        show={isOpen}
        onHide={onClose}
        size="lg"
        centered
        dialogClassName="custom-template-modal"
        contentClassName="custom-template-modal__content"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {initialTemplate
              ? `Edit custom template: ${initialTemplate.name}`
              : 'Add custom template'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="custom-template-modal__form">
            <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
              <div
                style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
              >
                <label
                  htmlFor="custom-template-name"
                  style={{ marginBottom: '8px' }}
                >
                  Template name
                </label>
                <input
                  id="custom-template-name"
                  type="text"
                  value={templateName}
                  onChange={(event) => setTemplateName(event.target.value)}
                  placeholder="Enter template name"
                />
              </div>
              <div
                style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
              >
                <label
                  htmlFor="custom-template-select"
                  style={{ marginBottom: '8px' }}
                >
                  Select template (optional)
                </label>
                <select
                  id="custom-template-select"
                  value={selectedTemplateId}
                  onChange={handleTemplateSelectChange}
                >
                  {templateOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="custom-template-modal__editor" ref={editorRef}>
            <div
              dangerouslySetInnerHTML={{
                __html: generateAIToolbarStyles(AI_ICON_COLOR, false),
              }}
            />
            <RichTextEditorComponent
              ref={rteRef}
              id="custom-template-editor"
              value={templateContent}
              change={(args) => setTemplateContent(args.value ?? '')}
              placeholder="Compose template..."
              toolbarSettings={toolbarSettings}
              height="400px"
            >
              <Inject
                services={[Toolbar, Link, Image, HtmlEditor, QuickToolbar]}
              />
            </RichTextEditorComponent>

            {activePopover === 'emoji' && (
              <div className="custom-template-modal__popover">
                <EmojiPicker
                  onEmojiClick={(emojiData) =>
                    handleEmojiSelect(emojiData.emoji)
                  }
                />
              </div>
            )}

            {activePopover === 'variable' && (
              <div className="custom-template-modal__popover">
                <div className="custom-template-modal__popover-title">
                  Insert variable
                </div>
                <div className="custom-template-modal__popover-list">
                  {VariableList.map((variable) => (
                    <button
                      key={variable.text}
                      type="button"
                      className="custom-template-modal__popover-item"
                      onClick={() => handleVariableSelect(variable.text)}
                    >
                      {variable.text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {activePopover === 'mention' && (
              <div className="custom-template-modal__popover">
                <div className="custom-template-modal__popover-title">
                  Mention patient
                </div>
                <div className="custom-template-modal__popover-list">
                  {mentionOptions.length > 0 ? (
                    mentionOptions.map((name) => (
                      <button
                        key={name}
                        type="button"
                        className="custom-template-modal__popover-item"
                        onClick={() => handleMentionSelect(name)}
                      >
                        {name}
                      </button>
                    ))
                  ) : (
                    <div className="custom-template-modal__popover-empty">
                      No patients available
                    </div>
                  )}
                </div>
              </div>
            )}

            {activePopover === 'magic' && (
              <div className="custom-template-modal__popover">
                <div className="custom-template-modal__popover-title">
                  AI Magic Edit
                </div>
                <div className="custom-template-modal__popover-list">
                  <button
                    type="button"
                    className="custom-template-modal__popover-item"
                    onClick={() => handleMagicEdit('reply')}
                  >
                    Draft a reply prompt
                  </button>
                  <button
                    type="button"
                    className="custom-template-modal__popover-item"
                    onClick={() => handleMagicEdit('summarize')}
                  >
                    Summarize this content
                  </button>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="custom-template-modal__footer">
          <div className="custom-template-modal__footer-left">
            {initialTemplate && onDelete && (
              <Button
                type="button"
                size="sm"
                className="button-destructive-primary"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete
              </Button>
            )}
          </div>
          <div className="custom-template-modal__footer-right">
            <Button
              type="button"
              size="sm"
              className="button-secondary"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="button-primary"
              onClick={handleSubmit}
              disabled={!templateName.trim()}
            >
              Save
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!templateToApply}
        onHide={() => setTemplateToApply(null)}
        centered
        className="delete-confirm-modal"
      >
        <Modal.Header className="bg-custom3" closeButton>
          <Modal.Title>Apply template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0 py-3">
            Discard current content and apply &quot;
            <b>{templateToApply?.name}</b>&quot;?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="md"
            className="button-secondary"
            onClick={() => setTemplateToApply(null)}
          >
            Cancel
          </Button>
          <Button
            size="md"
            className="button-primary"
            onClick={handleConfirmApplyTemplate}
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        centered
        className="delete-confirm-modal"
      >
        <Modal.Header className="bg-custom3" closeButton>
          <Modal.Title>Delete template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0 py-3">
            Are you sure you want to delete &quot;
            <b>{initialTemplate?.name}</b>&quot;? This cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="md"
            className="button-secondary"
            onClick={() => setShowDeleteConfirm(false)}
          >
            Cancel
          </Button>
          <Button
            size="md"
            className="button-destructive-primary"
            onClick={() => {
              if (initialTemplate?.id && onDelete) {
                setShowDeleteConfirm(false)
                onDelete(initialTemplate.id)
              }
            }}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default CustomTemplateModal
